import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Download,
  FolderOpen,
  Calendar,
  DollarSign,
  Users,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  TrendingUp,
  TrendingDown,
  Minus,
  Wallet,
  Archive,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

// Custom Dropdown Component
const ActionDropdown = ({
  project,
  onView,
  onEdit,
  onManageTeam,
  onViewBudget,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 280; // More accurate height estimate
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (project && (project.id || project._id)) {
      onView(project);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (project && (project.id || project._id)) {
      onEdit(project);
    }
  };

  const handleManageTeam = () => {
    setIsOpen(false);
    if (project && (project.id || project._id)) {
      onManageTeam(project);
    }
  };

  const handleViewBudget = () => {
    setIsOpen(false);
    if (project && (project.id || project._id)) {
      onViewBudget(project);
    }
  };

  // const handleArchive = () => {
  //   setIsOpen(false);
  //   if (project && (project.id || project._id)) {
  //     onArchive(project);
  //   }
  // };

  const handleDelete = () => {
    setIsOpen(false);
    // Timeout matches RiskManagement logic to ensure smooth UI transition
    setTimeout(() => {
        if (project && (project.id || project._id)) onDelete(project);
    }, 0);
  };

  return (
    <div 
      className="relative" 
      ref={dropdownRef}
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        type="button"
        onClick={handleToggle}
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} className="text-blue-500" />
      </button>
      
      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)', // Align menu to the right of button
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleView}
              >
                <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                View Project
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleEdit}
              >
                <Edit size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                Edit Project
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleManageTeam}
              >
                <Users size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                Manage Team
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={handleViewBudget}
              >
                <Wallet size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                View Budget
              </button>
            </li>
            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600 flex-shrink-0" />
                Delete Project
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // State management
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalRisks: 0,
    totalSpent: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Mock data for demonstration
  const mockProjects = [
    {
      id: 1,
      name: 'Digital Transformation Initiative',
      description:
        'Comprehensive digital transformation of core business processes',
      status: 'active',
      priority: 'high',
      startDate: '2024-01-15',
      endDate: '2024-06-30',
      budget: 250000,
      actualCost: 125000,
      progress: 45,
      projectManager: 'Sarah Johnson',
      team: ['John Smith', 'Mike Chen', 'Lisa Wang'],
      milestones: 8,
      completedMilestones: 3,
      risks: 2,
      issues: 1,
    },
    {
      id: 2,
      name: 'Infrastructure Modernization',
      description: 'Upgrade legacy systems and implement cloud infrastructure',
      status: 'active',
      priority: 'medium',
      startDate: '2024-02-01',
      endDate: '2024-08-15',
      budget: 180000,
      actualCost: 85000,
      progress: 32,
      projectManager: 'David Rodriguez',
      team: ['Alex Kim', 'Emma Davis', 'Tom Wilson'],
      milestones: 6,
      completedMilestones: 2,
      risks: 1,
      issues: 0,
    },
    {
      id: 3,
      name: 'Customer Experience Enhancement',
      description: 'Improve customer touchpoints and satisfaction metrics',
      status: 'planning',
      priority: 'high',
      startDate: '2024-03-01',
      endDate: '2024-09-30',
      budget: 120000,
      actualCost: 0,
      progress: 5,
      projectManager: 'Jennifer Lee',
      team: ['Mark Taylor', 'Sophie Brown'],
      milestones: 5,
      completedMilestones: 0,
      risks: 0,
      issues: 0,
    },
    {
      id: 4,
      name: 'Data Analytics Platform',
      description: 'Build comprehensive data analytics and reporting platform',
      status: 'active',
      priority: 'medium',
      startDate: '2024-01-10',
      endDate: '2024-07-20',
      budget: 200000,
      actualCost: 95000,
      progress: 28,
      projectManager: 'Robert Kim',
      team: ['Anna Garcia', 'Chris Johnson', 'Maria Lopez'],
      milestones: 7,
      completedMilestones: 2,
      risks: 3,
      issues: 2,
    },
    {
      id: 5,
      name: 'Security Compliance Audit',
      description: 'Comprehensive security audit and compliance implementation',
      status: 'completed',
      priority: 'high',
      startDate: '2023-10-01',
      endDate: '2024-01-31',
      budget: 75000,
      actualCost: 72000,
      progress: 100,
      projectManager: 'Michael Chen',
      team: ['Lisa Park', 'James Wilson'],
      milestones: 4,
      completedMilestones: 4,
      risks: 0,
      issues: 0,
    },
  ];

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Reload data when component comes back into focus
  useEffect(() => {
    const handleFocus = () => {
      loadProjects();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Load project statistics
  const loadProjectStats = async () => {
    try {
      // console.log(
      //   '📊 Attempting to fetch project stats from',
      //   API_CONFIG.ENDPOINTS.PROJECTS_STATS
      // );

      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS_STATS, {
        headers: API_CONFIG.HEADERS,
      });

      // console.log('📊 Stats response status:', response.status);
      // console.log('📊 Stats response ok:', response.ok);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        // console.log('📊 Stats Content-Type:', contentType);

        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text();
          console.warn(
            '📊 Stats API returned non-JSON response:',
            responseText
          );
          return;
        }

        const stats = await response.json();
        // console.log('✅ Stats data received:', stats);
        setStats({
          totalProjects: stats.totalProjects || 0,
          activeProjects: stats.activeProjects || 0,
          totalRisks: stats.totalRisks || 0,
          totalSpent: stats.totalSpent || 0,
        });
      } else {
        console.warn(
          '📊 Failed to fetch project stats:',
          response.status,
          response.statusText
        );
        // Keep default stats if API fails
      }
    } catch (error) {
      console.warn('📊 Error loading project stats:', error);
      // Keep default stats if API fails
    }
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      // console.log(
      //   '🔍 Attempting to fetch projects from',
      //   API_CONFIG.ENDPOINTS.PROJECTS
      // );
      // console.log(
      //   '🔑 Auth token:',
      //   localStorage.getItem('token') ? 'Present' : 'Missing'
      // );

      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS,
      });

      // console.log('📡 Response status:', response.status);
      // console.log(
      //   '📡 Response headers:',
      //   Object.fromEntries(response.headers.entries())
      // );
      // console.log('📡 Response ok:', response.ok);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        // console.log('📄 Content-Type:', contentType);

        if (!contentType || !contentType.includes('application/json')) {
          // Log the actual response text to see what we're getting
          const responseText = await response.text();
          console.error('❌ Non-JSON response received:', responseText);
          throw new Error('Server returned non-JSON response');
        }

        const data = await response.json();
        // console.log('✅ Projects data received:', data);
        // Handle both old format { projects, pagination } and new format { success, data: { projects }, pagination }
        const projects = data.data?.projects || data.projects || [];
        setProjects(projects);
        setPagination((prev) => ({
          ...prev,
          totalItems: data.pagination?.totalItems || 0,
          totalPages: data.pagination?.totalPages || 0,
          currentPage: data.pagination?.currentPage || 1,
        }));

        // Load stats after projects are loaded
        await loadProjectStats();
      } else {
        // Handle different error status codes
        if (response.status === 404) {
          throw new Error(
            'Projects API endpoint not found. Please ensure the backend server is running.'
          );
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          const text = await response.text();
          try {
            const errorData = JSON.parse(text);
            throw new Error(
              errorData.message ||
                `HTTP ${response.status}: ${response.statusText}`
            );
          } catch (parseError) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        }
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      showError(`Failed to load projects: ${err.message}. Using sample data.`);
      // Fallback to mock data
      setProjects(mockProjects);
      setPagination((prev) => ({
        ...prev,
        totalItems: mockProjects.length,
        totalPages: Math.ceil(mockProjects.length / prev.itemsPerPage),
      }));

      // Set mock stats when using fallback data
      setStats({
        totalProjects: mockProjects.length,
        activeProjects: mockProjects.filter((p) => p.status === 'active')
          .length,
        totalRisks: mockProjects.reduce(
          (sum, p) =>
            sum + (Array.isArray(p.risks) ? p.risks.length : p.risks || 0),
          0
        ),
        totalSpent: mockProjects.reduce(
          (sum, p) => sum + (p.actualCost || 0),
          0
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    navigate('/projects/project-management/create');
  };

  const handleRefresh = () => {
    loadProjects();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/project-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh projects list',
      disabled: loading,
    },
    {
      label: 'New Project',
      icon: <Plus size={18} />,
      onClick: handleCreateProject,
      variant: 'primary',
      tooltip: 'Create a new project',
    },
  ];

  const handleEditProject = (project) => {
    console.log('handleEditProject called with:', project);
    const projectId = project._id || project.id;
    console.log('Project ID:', projectId);
    if (projectId) {
      console.log('Navigating to:', `/projects/edit/${projectId}`);
      navigate(`/projects/project-management/edit/${projectId}`);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to edit project: ID not found');
    }
  };

  const handleViewProject = (project) => {
    const projectId = project._id || project.id;
    if (projectId) {
      navigate(`/projects/project-management/view/${projectId}`);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to view project: ID not found');
    }
  };

  const handleDeleteProject = (project) => {
    const projectId = project._id || project.id;
    if (projectId) {
      setProjectToDelete(project);
      setShowDeleteModal(true);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to delete project: ID not found');
    }
  };

  const handleManageTeam = (project) => {
    const projectId = project._id || project.id;
    if (projectId) {
      navigate(`/projects/project-management/view/${projectId}?tab=team`);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to manage team: ID not found');
    }
  };

  const handleViewBudget = (project) => {
    const projectId = project._id || project.id;
    if (projectId) {
      navigate(`/projects/project-management/view/${projectId}?tab=finance`);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to view budget: ID not found');
    }
  };

  const handleArchive = (project) => {
    const projectId = project._id || project.id;
    if (projectId) {
      setProjectToArchive(project);
      setShowArchiveModal(true);
    } else {
      console.error('Project ID not found:', project);
      showError('Unable to archive project: ID not found');
    }
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    setIsDeleting(true);
    try {
      const projectId = projectToDelete._id || projectToDelete.id;
      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}`,
        {
          method: 'DELETE',
          headers: API_CONFIG.HEADERS,
        }
      );

      if (response.ok) {
        success('Project deleted successfully!');
        // Reload projects and stats
        await loadProjects();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete project');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      showError(err.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  const confirmArchive = async () => {
    if (!projectToArchive) return;

    setIsArchiving(true);
    try {
      const projectId = projectToArchive._id || projectToArchive.id;
      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}`,
        {
          method: 'DELETE',
          headers: API_CONFIG.HEADERS,
        }
      );

      if (response.ok) {
        success('Project archived successfully!');
        // Reload projects and stats
        await loadProjects();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to archive project');
      }
    } catch (err) {
      console.error('Error archiving project:', err);
      showError(err.message || 'Failed to archive project');
    } finally {
      setIsArchiving(false);
      setShowArchiveModal(false);
      setProjectToArchive(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', bg: '#d1fae5', text: 'Active' },
      planning: { color: '#f59e0b', bg: '#fef3c7', text: 'Planning' },
      onHold: { color: '#6b7280', bg: '#f3f4f6', text: 'On Hold' },
      completed: { color: '#3b82f6', bg: '#dbeafe', text: 'Completed' },
      cancelled: { color: '#ef4444', bg: '#fee2e2', text: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
        }}
      >
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{
            backgroundColor: config.color,
          }}
        />
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { color: '#dc2626', bg: '#fee2e2', text: 'High' },
      medium: { color: '#f59e0b', bg: '#fef3c7', text: 'Medium' },
      low: { color: '#10b981', bg: '#d1fae5', text: 'Low' },
    };

    const config = priorityConfig[priority] || priorityConfig.medium;

    return (
      <span
        className="inline-block px-1.5 py-0.5 text-xs font-medium rounded"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
        }}
      >
        {config.text}
      </span>
    );
  };

  const getProgressColor = (progress) => {
    if (progress >= 70) return '#10b981'; // Green for >70%
    if (progress >= 30) return '#f59e0b'; // Yellow for 30-70%
    return '#ef4444'; // Red for <30%
  };

  // Format number with US locale
  const formatUSCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatUSNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'name',
      label: 'Project Name',
      render: (value, project) => {
        // Project name and description rendering
        return (
          <div>
            <div className="font-semibold text-gray-900 text-sm mb-0.5">
              {project.name || project.projectName || project._id || 'No Name'}
            </div>
            <div className="text-gray-500 text-xs leading-relaxed max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
              {project.description ||
                project.projectDescription ||
                'No Description'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, project) => getStatusBadge(project.status),
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (project) => getPriorityBadge(project.priority),
    },
    {
      key: 'startDate',
      label: 'Start Date',
      render: (value, project) => {
        const startDate = project.startDate || project.start_date;
        return (
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-gray-500 flex-shrink-0" />
            <span className="text-xs text-gray-700">
              {formatDate(startDate)}
            </span>
          </div>
        );
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      render: (value, project) => {
        const endDate = project.endDate || project.end_date;
        return (
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-gray-500 flex-shrink-0" />
            <span className="text-xs text-gray-700">{formatDate(endDate)}</span>
          </div>
        );
      },
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (project) => {
        const progress = project.progress || 0;
        const completedMilestones = project.completedMilestones || 0;
        const totalMilestones = project.milestones || 0;
        const progressColor = getProgressColor(progress);

        return (
          <div
            className="relative"
            title={`${completedMilestones} of ${totalMilestones} milestones completed`}
          >
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-gray-700">
                {progress}%
              </span>
              <span className="text-xs text-gray-500">
                {completedMilestones} / {totalMilestones} milestones
              </span>
            </div>
            <div className="w-full h-2 bg-gray-300 rounded overflow-hidden relative">
              <div
                className="h-full rounded transition-all duration-300"
                style={{
                  width: `${Math.min(progress, 100)}%`,
                  backgroundColor: progressColor,
                  boxShadow:
                    progress > 0 ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'budget',
      label: 'Budget',
      render: (value, project) => {
        const actualCost = project.actualCost || 0;
        const budget = project.budget || 0;
        return (
          <div>
            <div className="font-semibold text-gray-900 text-sm">
              {formatUSCurrency(actualCost)}
            </div>
            <div className="text-gray-500 text-xs">
              of {formatUSCurrency(budget)}
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, project) => {
        // Actions column rendering

        if (!project || (!project.id && !project._id)) {
          console.error('Invalid project data in render function:', project);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <div className="flex justify-start">
            <ActionDropdown
              project={project}
              onView={handleViewProject}
              onEdit={handleEditProject}
              onManageTeam={handleManageTeam}
              onViewBudget={handleViewBudget}
              onDelete={handleDeleteProject}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Project Management"
        description="Manage and track all your active projects"
        actions={headerActions}
      />
      <div className="page-container">
        {/* Projects Table */}
        <DataTable
          data={projects}
          columns={columns}
          title="Projects"
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          emptyMessage="No projects found. Create your first project to get started."
          emptyIcon={<FolderOpen size={48} />}
          showActions={false}
        />

        {/* Modals */}
        <ConfirmationModal
          isOpen={showDeleteModal}           // Was 'show'
          onClose={() => setShowDeleteModal(false)} // Was 'onHide'
          onConfirm={confirmDelete}
          title="Delete Project"
          message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"                      // Was 'variant'
          isLoading={isDeleting}             // Was 'loading'
        />

        <ConfirmationModal
          show={showArchiveModal}
          onHide={() => setShowArchiveModal(false)}
          onConfirm={confirmArchive}
          title="Archive Project"
          message={`Are you sure you want to archive "${projectToArchive?.name}"? The project will be hidden from the active projects list but can be restored later.`}
          confirmText="Archive"
          cancelText="Cancel"
          variant="warning"
          loading={isArchiving}
        />

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ProjectManagement;
